import React, { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '../lib/supabase';
import { ChevronRight, ChevronDown, Search } from 'lucide-react';
import { fetchAnnotationCountMapsV2 } from '../lib/annotationV2Persistence';
import '../styles/ProjectKeyPanelTree.css';

interface ComponentKey {
  color: string;
  name: string;
  symbol: string;
  pages: Array<{ page: number; count: number }>;
  totalCount: number;
  referenceImage?: string;
  hasReferenceImage?: boolean;
  symbolType?: 'power' | 'lighting' | 'fire_alarm' | 'communications';
  componentType?: string;  // Classification: 'Switches', 'Receptacles', 'Distribution Equipment', 'Telecom', 'Lighting', 'Fire Alarm', 'Low Voltage', 'Other'
  description?: string;  // Full technical description with subscripts, notations, specs
  subscript?: string;  // Subscript/suffix/variant indicator (e.g., '3' from S₃, 'WP' from SWP, '' if none)
  category?: 'symbols' | 'wires';
  laborUnits?: number;
  materialCost?: number;
  estimate_matched_component?: string; // NECA matched component for estimate table correlation
}

interface ProjectKey {
  components: ComponentKey[];
  extractedAt: string;
  pdfHash?: string;
}

interface TreeNode {
  id: string;
  name: string;
  type: 'category' | 'type' | 'component';
  count?: number;
  children?: TreeNode[];
  data?: ComponentKey;
  expanded?: boolean;
}

interface ProjectKeyPanelTreeProps {
  estimationId: string;
  onComponentSelect?: (component: ComponentKey) => void;
  onKeyUpdate?: (key: ProjectKey) => void;
  pdfAnnotatorRef?: React.RefObject<any>;
  currentFile?: any;
  onCollapse?: () => void;
  onAnnotationCountsChange?: (counts: Map<string, Map<number, number>>) => void;
  refreshTrigger?: number;  // Increment to force immediate count refresh after save
  extractionProgress?: string;
  onStartExtraction?: () => void;
}

// Color mapping for symbol types
const SYMBOL_TYPE_COLORS = {
  power: '#22c55e',
  lighting: '#eab308',
  fire_alarm: '#ef4444',
  communications: '#3b82f6'
};

const SYMBOL_TYPE_LABELS = {
  power: 'Power',
  lighting: 'Lighting',
  fire_alarm: 'Fire Alarm',
  communications: 'Communications'
};

export const ProjectKeyPanelTree: React.FC<ProjectKeyPanelTreeProps> = ({
  estimationId,
  onComponentSelect,
  onKeyUpdate,
  pdfAnnotatorRef,
  currentFile,
  onCollapse,
  onAnnotationCountsChange,
  refreshTrigger,
  extractionProgress: externalProgress,
  onStartExtraction
}) => {
  const [projectKey, setProjectKey] = useState<ProjectKey | null>(null);
  const [annotationCounts, setAnnotationCounts] = useState<Map<string, Map<number, number>>>(new Map());
  const [componentImages, setComponentImages] = useState<Map<string, string>>(new Map());
  const [searchQuery, setSearchQuery] = useState('');
  const [treeData, setTreeData] = useState<TreeNode[]>([]);
  const [selectedNode, setSelectedNode] = useState<TreeNode | null>(null);
  const [expandedNodes, setExpandedNodes] = useState<Set<string>>(new Set(['symbols']));
  
  const projectKeyRef = useRef<ProjectKey | null>(null);

  useEffect(() => {
    projectKeyRef.current = projectKey;
  }, [projectKey]);

  useEffect(() => {
    console.log('🎯 ProjectKeyPanelTree useEffect fired:', {
      hasCallback: !!onAnnotationCountsChange,
      countsSize: annotationCounts.size,
      components: Array.from(annotationCounts.keys())
    });
    
    if (onAnnotationCountsChange) {
      console.log('📞 Calling onAnnotationCountsChange callback');
      onAnnotationCountsChange(annotationCounts);
    } else {
      console.warn('⚠️ No onAnnotationCountsChange callback provided');
    }
  }, [annotationCounts, onAnnotationCountsChange]);

  const loadProjectKey = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from('estimations')
        .select('project_key')
        .eq('id', estimationId)
        .single();

      if (error) {
        console.error('Error loading project key:', error);
        return;
      }

      if (data?.project_key) {
        setProjectKey(data.project_key);
      }
    } catch (err) {
      console.error('Failed to load project key:', err);
    }
  }, [estimationId]);

  const fetchAnnotationCounts = useCallback(async () => {
    try {
      const counts = await fetchAnnotationCountMapsV2({
        supabase: supabase as any,
        estimationId,
      });
      setAnnotationCounts(counts);
    } catch (err) {
      console.error('Failed to fetch annotation counts:', err);
    }
  }, [estimationId]);

  useEffect(() => {
    if (!estimationId) return;
    
    const loadKeyAndCounts = async () => {
      await loadProjectKey();
      await fetchAnnotationCounts();
    };
    
    loadKeyAndCounts();

    console.log('🔔 Setting up real-time subscription for annotations...');
    
    const channel = supabase
      .channel(`annotations-tree-${estimationId}`, {
        config: {
          broadcast: { self: true }
        }
      })
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'annotation_entities_v2',
          filter: `estimation_id=eq.${estimationId}`
        },
        (payload) => {
          console.log('🔥 REAL-TIME UPDATE RECEIVED:', payload);
          console.log('📊 Annotation change detected, refreshing counts...');
          loadProjectKey(); // Reload key to get any new component types
          fetchAnnotationCounts();
        }
      )
      .subscribe((status, err) => {
        console.log('🔔 Subscription status:', status);
        if (err) {
          console.error('❌ Subscription error:', err);
        }
        if (status === 'SUBSCRIBED') {
          console.log('✅ Successfully subscribed to annotation changes');
        } else if (status === 'CHANNEL_ERROR') {
          console.error('❌ Channel error - Realtime might not be enabled');
        } else if (status === 'TIMED_OUT') {
          console.error('⏱️ Subscription timed out');
        }
      });

    // Fallback polling every 5 seconds in case real-time doesn't work
    const pollInterval = setInterval(() => {
      console.log('🔄 Fallback poll: Checking for annotation updates...');
      fetchAnnotationCounts();
    }, 5000);

    return () => {
      console.log('🔕 Unsubscribing from real-time updates');
      clearInterval(pollInterval);
      supabase.removeChannel(channel);
    };
  }, [estimationId, fetchAnnotationCounts, loadProjectKey]);

  // Immediate refresh path triggered by parent after annotation saves
  useEffect(() => {
    if (!estimationId) return;
    if (refreshTrigger === undefined) return;

    console.log('🔄 Immediate refresh from parent trigger:', refreshTrigger);
    loadProjectKey();
    fetchAnnotationCounts();
  }, [refreshTrigger, estimationId, loadProjectKey, fetchAnnotationCounts]);

  // Load images from project_key directly
  useEffect(() => {
    if (!projectKey?.components) return;
    
    const images = new Map<string, string>();
    projectKey.components.forEach(comp => {
      if (comp.referenceImage) {
        // Images are already in base64 format in project_key
        images.set(comp.name, comp.referenceImage);
        console.log(`📸 Loaded image for: "${comp.name}" (${comp.referenceImage.substring(0, 50)}...)`);
      }
    });
    
    console.log(`📸 Total loaded: ${images.size} component images`);
    console.log('Map keys:', Array.from(images.keys()));
    setComponentImages(images);
  }, [projectKey]);

  // Build tree structure from project key
  const buildTree = useCallback((key: ProjectKey | null, counts: Map<string, Map<number, number>>, search: string): TreeNode[] => {
    if (!key) return [];

    const components = key.components || [];
    const searchLower = search.toLowerCase();

    // Group components by category and type
    const symbolsByType: { [type: string]: ComponentKey[] } = {};
    const wires: ComponentKey[] = [];

    components.forEach(comp => {
      // Filter by search if provided
      if (search && !comp.name.toLowerCase().includes(searchLower)) {
        return;
      }

      const category = comp.category || 'symbols';
      if (category === 'wires') {
        wires.push(comp);
      } else {
        const type = comp.symbolType || 'power';
        if (!symbolsByType[type]) {
          symbolsByType[type] = [];
        }
        symbolsByType[type].push(comp);
      }
    });

    const tree: TreeNode[] = [];

    // Symbols category
    if (Object.keys(symbolsByType).length > 0 || !search) {
      const symbolsChildren: TreeNode[] = [];
      
      Object.entries(symbolsByType).forEach(([type, comps]) => {
        const typeNode: TreeNode = {
          id: `type-${type}`,
          name: SYMBOL_TYPE_LABELS[type as keyof typeof SYMBOL_TYPE_LABELS] || type,
          type: 'type',
          count: comps.reduce((sum, c) => {
            const componentCounts = counts.get(c.name);
            if (!componentCounts) return sum;
            return sum + Array.from(componentCounts.values()).reduce((a, b) => a + b, 0);
          }, 0),
          children: comps.map(comp => {
            const componentCounts = counts.get(comp.name);
            const totalCount = componentCounts 
              ? Array.from(componentCounts.values()).reduce((sum, count) => sum + count, 0)
              : 0;
            
            return {
              id: `component-${comp.name}`,
              name: comp.name,
              type: 'component' as const,
              count: totalCount,
              data: comp
            };
          }),
          expanded: expandedNodes.has(`type-${type}`)
        };
        symbolsChildren.push(typeNode);
      });

      if (symbolsChildren.length > 0) {
        tree.push({
          id: 'symbols',
          name: 'Symbols',
          type: 'category',
          count: symbolsChildren.reduce((sum, t) => sum + (t.count || 0), 0),
          children: symbolsChildren,
          expanded: expandedNodes.has('symbols')
        });
      }
    }

    // Wires category
    if (wires.length > 0) {
      tree.push({
        id: 'wires',
        name: 'Wires',
        type: 'category',
        count: wires.reduce((sum, c) => {
          const componentCounts = counts.get(c.name);
          if (!componentCounts) return sum;
          return sum + Array.from(componentCounts.values()).reduce((a, b) => a + b, 0);
        }, 0),
        children: wires.map(comp => {
          const componentCounts = counts.get(comp.name);
          const totalCount = componentCounts 
            ? Array.from(componentCounts.values()).reduce((sum, count) => sum + count, 0)
            : 0;
          
          return {
            id: `component-${comp.name}`,
            name: comp.name,
            type: 'component' as const,
            count: totalCount,
            data: comp
          };
        }),
        expanded: expandedNodes.has('wires')
      });
    }

    return tree;
  }, [expandedNodes]);

  useEffect(() => {
    const tree = buildTree(projectKey, annotationCounts, searchQuery);
    setTreeData(tree);
  }, [projectKey, annotationCounts, searchQuery, buildTree]);

  const toggleNode = (nodeId: string) => {
    setExpandedNodes(prev => {
      const newSet = new Set(prev);
      if (newSet.has(nodeId)) {
        newSet.delete(nodeId);
      } else {
        newSet.add(nodeId);
      }
      return newSet;
    });
  };

  const handleNodeSelect = (node: TreeNode) => {
    console.log('🔍 Node selected:', node.name, 'Type:', node.type);
    
    // If clicking the same component, deselect it
    if (selectedNode?.id === node.id) {
      console.log('🔄 Deselecting component');
      setSelectedNode(null);
      // Clear filter in PDFAnnotator
      if (pdfAnnotatorRef?.current?.highlightComponentType) {
        pdfAnnotatorRef.current.highlightComponentType(null);
      }
      return;
    }
    
    if (node.type === 'component' && node.data) {
      console.log('🖼️ Component data:', {
        name: node.data.name,
        hasImage: !!componentImages.get(node.data.name),
        imagePreview: componentImages.get(node.data.name)?.substring(0, 60)
      });
      
      // Filter annotations to show only this component type
      if (pdfAnnotatorRef?.current?.highlightComponentType) {
        console.log('🎯 Filtering annotations to show only:', node.data.name);
        pdfAnnotatorRef.current.highlightComponentType(node.data.name);
      }
      
      onComponentSelect?.(node.data);
    } else {
      // If selecting a category or type node, clear the filter
      if (pdfAnnotatorRef?.current?.highlightComponentType) {
        pdfAnnotatorRef.current.highlightComponentType(null);
      }
    }
    
    setSelectedNode(node);
  };

  const getTotalCost = (component: ComponentKey): number => {
    const count = annotationCounts.get(component.name);
    const totalCount = count 
      ? Array.from(count.values()).reduce((sum, c) => sum + c, 0)
      : 0;
    
    const laborCost = (component.laborUnits || 0) * totalCount * 50; // $50/hour assumption
    const materialCost = (component.materialCost || 0) * totalCount;
    return laborCost + materialCost;
  };

  return (
    <div className="key-panel-tree">
      {/* Search Bar */}
      <div className="tree-search">
        <Search size={16} />
        <input
          type="text"
          placeholder="Search components..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
      </div>

      {/* Tree Navigation */}
      <div className="tree-container">
            {treeData.length === 0 ? (
              <div className="tree-empty">
                <p>No power components found</p>
                {onStartExtraction && (
                  <button onClick={onStartExtraction} className="extract-btn">
                    Extract Power Legend
                  </button>
                )}
              </div>
            ) : (
              treeData.map(node => (
                <TreeNodeComponent
                  key={node.id}
                  node={node}
                  level={0}
                  onSelect={handleNodeSelect}
                  onToggle={toggleNode}
                  selectedId={selectedNode?.id}
                  componentImages={componentImages}
                />
              ))
            )}
          </div>

          {/* Details Panel - Compact 2-Row Layout */}
          {selectedNode?.type === 'component' && selectedNode.data && (
            <div className="component-details">
              {/* Symbol Image - Full Width */}
              <div 
                className="detail-symbol-container"
                draggable={true}
                onDragStart={(e) => {
                  // Store component data for drop
                  if (!selectedNode.data) return;
                  e.dataTransfer.setData('component', JSON.stringify({
                    name: selectedNode.data.name,
                    symbol: selectedNode.data.symbol,
                    color: selectedNode.data.color
                  }));
                  e.dataTransfer.effectAllowed = 'copy';
                  
                  // Create custom drag image - a styled div with a circle
                  const dragPreview = document.createElement('div');
                  dragPreview.style.width = '50px';
                  dragPreview.style.height = '50px';
                  dragPreview.style.borderRadius = '50%';
                  dragPreview.style.backgroundColor = selectedNode.data.color;
                  dragPreview.style.border = `3px solid ${selectedNode.data.color}`;
                  dragPreview.style.boxShadow = `0 0 10px ${selectedNode.data.color}80`;
                  dragPreview.style.position = 'absolute';
                  dragPreview.style.top = '-1000px';
                  dragPreview.style.left = '-1000px';
                  
                  // Append to body temporarily
                  document.body.appendChild(dragPreview);
                  
                  // Set as drag image
                  e.dataTransfer.setDragImage(dragPreview, 25, 25);
                  
                  // Remove after a short delay
                  setTimeout(() => {
                    document.body.removeChild(dragPreview);
                  }, 0);
                }}
                style={{ cursor: 'grab' }}
              >
                {componentImages.get(selectedNode.data.name) ? (
                  <img 
                    src={componentImages.get(selectedNode.data.name)} 
                    alt={selectedNode.data.name}
                    className="detail-symbol-image"
                    onError={(e) => {
                      console.log('❌ Image failed to load for:', selectedNode.data?.name || 'unknown component');
                      console.log('Image URL:', componentImages.get(selectedNode.data?.name || ''));
                    }}
                    style={{ pointerEvents: 'none' }} // Prevent img from interfering with drag
                  />
                ) : (
                  <div className="detail-symbol-placeholder">
                    <div className="placeholder-icon">
                      <svg width="80" height="80" viewBox="0 0 80 80" fill="none">
                        <rect x="10" y="10" width="60" height="60" rx="8" stroke="var(--border-strong)" strokeWidth="2" fill="var(--bg-tertiary)"/>
                        <path d="M30 40L40 50L50 30" stroke="var(--text-placeholder)" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </div>
                    <p className="placeholder-text">No image</p>
                  </div>
                )}
              </div>
              
              {/* Stats Row - All in One Horizontal Line */}
              <div className="detail-stats-row">
                <div className="stat-item">
                  <span className="stat-label">COUNT</span>
                  <strong className="stat-value">{selectedNode.count || 0}</strong>
                </div>
                <div className="stat-item">
                  <span className="stat-label">LABOR UNITS</span>
                  <strong className="stat-value">{selectedNode.data.laborUnits || 0}</strong>
                </div>
                <div className="stat-item">
                  <span className="stat-label">MATERIAL COST</span>
                  <strong className="stat-value">${(selectedNode.data.materialCost || 0).toFixed(2)}</strong>
                </div>
                <div className="stat-item stat-total">
                  <span className="stat-label">TOTAL</span>
                  <strong className="stat-value">${getTotalCost(selectedNode.data).toFixed(2)}</strong>
                </div>
              </div>
            </div>
          )}

      {/* Extraction Progress */}
      {externalProgress && (
        <div className="extraction-progress">
          {externalProgress}
        </div>
      )}
    </div>
  );
};

// Tree Node Component
const TreeNodeComponent: React.FC<{
  node: TreeNode;
  level: number;
  onSelect: (node: TreeNode) => void;
  onToggle: (nodeId: string) => void;
  selectedId?: string;
  componentImages: Map<string, string>;
}> = ({ node, level, onSelect, onToggle, selectedId, componentImages }) => {
  const hasChildren = node.children && node.children.length > 0;
  const isExpanded = node.expanded;

  const handleClick = () => {
    if (hasChildren) {
      onToggle(node.id);
    }
    onSelect(node);
  };

  const getTypeColor = (node: TreeNode): string | undefined => {
    if (node.type === 'type') {
      const typeKey = node.id.replace('type-', '');
      return SYMBOL_TYPE_COLORS[typeKey as keyof typeof SYMBOL_TYPE_COLORS];
    }
    if (node.type === 'component' && node.data?.symbolType) {
      return SYMBOL_TYPE_COLORS[node.data.symbolType];
    }
    return undefined;
  };

  const typeColor = getTypeColor(node);

  return (
    <div className="tree-node">
      <div 
        className={`node-label level-${level} ${selectedId === node.id ? 'selected' : ''} ${node.type}`}
        style={{ paddingLeft: `${level * 20 + 12}px` }}
        onClick={handleClick}
      >
        {hasChildren && (
          <span className="expand-icon">
            {isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
          </span>
        )}
        {!hasChildren && <span className="bullet">•</span>}
        
        {typeColor && (
          <span 
            className="type-indicator" 
            style={{ backgroundColor: typeColor }}
          />
        )}
        
        <span className="node-name">{node.name}</span>
        
        {node.count !== undefined && node.count > 0 && (
          <span className="node-count">{node.count}</span>
        )}
      </div>
      
      {isExpanded && hasChildren && (
        <div className="node-children">
          {node.children!.map(child => (
            <TreeNodeComponent
              key={child.id}
              node={child}
              level={level + 1}
              onSelect={onSelect}
              onToggle={onToggle}
              selectedId={selectedId}
              componentImages={componentImages}
            />
          ))}
        </div>
      )}
    </div>
  );
};

